import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router';
import { motion, useInView, AnimatePresence } from 'motion/react';
import {
  Shield, Brain, BarChart3, Users, Target, Award,
  CheckCircle2, ArrowRight, Star, Play, Zap,
  FileCheck, TrendingUp, Headphones, BookOpen,
  ChevronDown, Menu, X, MessageSquare, Clock,
  Lock, Globe, Layers, Sparkles, AlertTriangle,
  Volume2, Trophy, Database, GitBranch, Gauge,
  BadgeCheck, Mic, AudioLines, ShieldAlert,
  CircleDot, Crown, Medal
} from 'lucide-react';
import { ImageWithFallback } from '../components/figma/ImageWithFallback';
import logoImg from '../../assets/a50295a0af56ffb8dcd053f3d73ca9d37c948ba6.png';
import logoDarkImg from '../../assets/ff6cec488a700597f19b04a772a6a682dadfe9e6.png';

// ─── Hook: detectar tema ───
function useThemeDetector() {
  const [isDark, setIsDark] = useState(() => document.documentElement.getAttribute('data-theme') === 'dark');
  useEffect(() => {
    const obs = new MutationObserver(() => setIsDark(document.documentElement.getAttribute('data-theme') === 'dark'));
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    return () => obs.disconnect();
  }, []);
  return isDark;
}

// ─── Hook: animated counter ───
function useCounter(target: number, duration = 2000, startOnView = true) {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref as React.RefObject<Element>, { once: true });
  useEffect(() => {
    if (!startOnView || !inView) return;
    let start = 0;
    const step = target / (duration / 16);
    const timer = setInterval(() => {
      start += step;
      if (start >= target) { setCount(target); clearInterval(timer); }
      else setCount(Math.floor(start));
    }, 16);
    return () => clearInterval(timer);
  }, [inView, target, duration, startOnView]);
  return { count, ref };
}

// ─── Simulator chat sequence data ───
const SIMULATOR_SEQUENCE = [
  {
    type: 'client' as const,
    label: 'Cliente IA (irritado)',
    text: 'Ja e a terceira vez que ligo! Meu pedido esta atrasado ha 5 dias e ninguem resolve nada. Quero cancelar TUDO agora!',
    delay: 800,
  },
  {
    type: 'operator' as const,
    label: 'Operador (voce)',
    text: 'Entendo perfeitamente sua frustracao, Sr. Carlos. Voce tem toda razao em estar insatisfeito. Vou resolver isso agora mesmo...',
    delay: 2000,
  },
];

// ═══════════════════════════════════════════════════════
// DADOS BASEADOS NO VALUE PROPOSITION
// ═══════════════════════════════════════════════════════

const PILLARS = [
  {
    id: 'simulator',
    icon: Headphones,
    badge: 'Pilar 1',
    title: 'Simulador de Voo para Atendimento',
    subtitle: 'Sandbox de Performance — Seguranca no Erro',
    painPoint: 'Dor: Curva de aprendizado lenta e subjetiva',
    description: 'Ambiente controlado onde a falha ocorre sem impactar o CX real. A IA simula cenarios fixos (Retencao, Atraso, Cobranca) e entrega o Loop de Maestria "3+3": feedback estruturado em JSON com 3 pontos positivos e 3 de melhoria no exato momento da conclusao.',
    highlights: [
      'Cenarios fixos: Retencao, Atraso, Cobranca — expansiveis',
      'Feedback 3+3 em JSON estruturado, imediato e tecnico',
      'Descomprime lideranca: supervisor vira estrategista de dados',
      'Operador pratica sem supervisao humana, quantas vezes precisar',
    ],
    color: 'var(--secondary)',
    metric: { value: '92%', label: 'dos operadores melhoram em 7 dias' },
  },
  {
    id: 'falha-grave',
    icon: ShieldAlert,
    badge: 'Pilar 2',
    title: 'Falha Grave Zera',
    subtitle: 'Regra Deterministica — Protecao da Marca',
    painPoint: 'Dor: Erros criticos que geram processos e multas',
    description: 'Regra deterministica via boolean isFalhaGrave no scorecard. Um unico erro critico de conduta ou regulatorio anula a nota automaticamente para ZERO. O trecho exato da transcricao e anexado como prova tecnica. Sem subjetividade, sem margem.',
    highlights: [
      'Boolean isFalhaGrave: regra deterministica no scorecard',
      'Nota zerada automaticamente — prova tecnica anexada',
      'Previne erros juridicos, de conduta e regulatorios',
      'Dashboard com Top 3 falhas graves mais frequentes por setor',
    ],
    color: 'var(--error)',
    metric: { value: '-72%', label: 'queda nas zeradas em 4 semanas' },
  },
  {
    id: 'audit-ia',
    icon: AudioLines,
    badge: 'Pilar 3',
    title: 'Auditoria de Massa via IA',
    subtitle: 'Cobertura Total — De 5% para 100%',
    painPoint: 'Dor: Amostragem irrisoria de 5% das chamadas',
    description: 'Processamento assincrono de audios MP3/WAV com transcricao automatica. A IA aplica o mesmo scorecard deterministico usado por humanos. Status visual claro: Pendente → Processando → Pronto → Validado. Transcricao exibida lado a lado com o scorecard.',
    highlights: [
      'Upload em lote de MP3/WAV com processamento assincrono',
      'Mesmo scorecard deterministico aplicado por humanos e IA',
      'Status visual: Pendente → Processando → Pronto → Validado',
      'Transcricao contextual lado a lado com itens avaliados',
    ],
    color: 'var(--primary)',
    metric: { value: '100%', label: 'de cobertura vs 5% manual' },
  },
  {
    id: 'gamification',
    icon: Trophy,
    badge: 'Pilar 4',
    title: 'Gamificacao & Engajamento',
    subtitle: 'Ciclo de Retencao — Ligas Inviolaveis',
    painPoint: 'Dor: Baixo engajamento e alta rotatividade',
    description: 'Sistema de Ligas (Bronze a Platinum) com badges e certificados automaticos inviolaveis. O operador e motivado a completar sessoes para conquistar marcos como "Nota Perfeita" ou "Mestre do Atendimento". Certificacao emitida sem intervencao manual do gestor.',
    highlights: [
      'Ligas competitivas: Bronze, Prata, Ouro, Platinum',
      'Badges inviolaveis: "Nota Perfeita", "Mestre do Atendimento"',
      'Certificados automaticos por marco de performance',
      'Valoriza carreira do colaborador — zero custo de gestao',
    ],
    color: 'var(--warning)',
    metric: { value: '3.4x', label: 'mais engajamento vs metodos tradicionais' },
  },
  {
    id: 'data',
    icon: Database,
    badge: 'Pilar 5',
    title: 'Integridade de Dados & Hierarquia',
    subtitle: 'Historico Cronologico Inviolavel',
    painPoint: 'Dor: Perda de historico em mudancas de equipe',
    description: 'Arquitetura com 38 tabelas de integridade referencial e historico cronologico de hierarquia. Mesmo que um operador mude de equipe, seus resultados ficam vinculados ao supervisor correto da epoca. Isolamento multi-tenant via RLS no nivel do banco de dados.',
    highlights: [
      'Historico de hierarquia preservado por periodo — inviolavel',
      'KPIs historicos nunca sao corrompidos por mudancas de equipe',
      '38 tabelas com integridade referencial total e RLS',
      'Isolamento multi-tenant no nivel mais profundo do banco',
    ],
    color: 'var(--primary)',
    metric: { value: '100%', label: 'rastreabilidade de dados' },
  },
];

const STATS = [
  { value: 72, suffix: '%', prefix: '-', label: 'Queda nas Zeradas', sublabel: 'por falha grave em 4 semanas' },
  { value: 100, suffix: '%', prefix: '', label: 'Cobertura de Auditorias', sublabel: 'vs 5% no modelo manual' },
  { value: 92, suffix: '%', prefix: '', label: 'Operadores Melhoram', sublabel: 'apos primeira semana no simulador' },
  { value: 10, suffix: 'x', prefix: '', label: 'Mais Auditorias IA', sublabel: 'Professional vs Starter' },
];

const TESTIMONIALS = [
  {
    name: 'Ana Cristina Mendes',
    role: 'Gerente de Qualidade',
    company: 'TeleConnect Brasil',
    image: 'https://images.unsplash.com/photo-1581065178047-8ee15951ede6?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjB3b21hbiUyMGJ1c2luZXNzJTIwcG9ydHJhaXR8ZW58MXx8fHwxNzcyMzc0MDY4fDA&ixlib=rb-4.1.0&q=80&w=1080',
    text: 'Auditavamos 5% das ligacoes. Hoje auditamos 100% com a IA e as zeradas por falha grave cairam 72% em apenas 4 semanas.',
    rating: 5,
  },
  {
    name: 'Ricardo Almeida',
    role: 'Diretor de Operacoes',
    company: 'ServiMax Contact Center',
    image: 'https://images.unsplash.com/photo-1648448942225-7aa06c7e8f79?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMG1hbiUyMGxhdGluJTIwY29ycG9yYXRlJTIwcG9ydHJhaXR8ZW58MXx8fHwxNzcyNDA0MjIyfDA&ixlib=rb-4.1.0&q=80&w=1080',
    text: 'O conceito de simulador de voo vendeu nosso board na hora. Operador nao erra com cliente real.',
    rating: 5,
  },
  {
    name: 'Juliana Ferreira',
    role: 'Supervisora de Atendimento',
    company: 'NovaSaude Seguros',
    image: 'https://images.unsplash.com/photo-1689600944138-da3b150d9cb8?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHx3b21hbiUyMGV4ZWN1dGl2ZSUyMHBvcnRyYWl0JTIwaGVhZHNob3R8ZW58MXx8fHwxNzcyNDAzOTI1fDA&ixlib=rb-4.1.0&q=80&w=1080',
    text: 'O Sistema de Ligas mudou tudo. Operadores competem por badges e os certificados automaticos valorizaram a carreira deles sem esforco manual da gestao.',
    rating: 5,
  },
];

const PLANS = [
  {
    name: 'Starter',
    price: 'R$ 497',
    period: '/mes',
    description: 'Valide a regra Falha Grave Zera na sua operacao.',
    trigger: 'Foco em validacao da regra Falha Grave Zera',
    features: [
      'Ate 30 operadores',
      'Auditoria manual ilimitada',
      '500 auditorias IA/mes',
      'Simulador de treino IA (3 cenarios)',
      'Falha Grave Zera ativado',
      'Relatorios basicos',
      'Suporte por e-mail',
    ],
    highlighted: false,
    cta: 'Iniciar Teste Gratis de 14 dias',
  },
  {
    name: 'Professional',
    price: 'R$ 1.297',
    period: '/mes',
    description: '10x mais auditorias IA. Escala, transcricao e gamificacao.',
    trigger: 'Escala + Transcricao IA + Gamificacao completa',
    features: [
      'Ate 150 operadores',
      '5.000 auditorias IA/mes (10x Starter)',
      'Transcricao automatica de audio (MP3/WAV)',
      'Simulador IA com cenarios customizados',
      'Gamificacao completa (Ligas + Badges)',
      'Certificacao automatica inviolavel',
      'Analytics avancado + Dashboards por setor',
      'Historico hierarquico cronologico',
      'Suporte prioritario',
    ],
    highlighted: true,
    cta: 'Iniciar Teste Gratis de 14 dias',
  },
  {
    name: 'Enterprise',
    price: 'Sob consulta',
    period: '',
    description: 'API dedicada, SSO/SAML, White Label e isolamento RLS.',
    trigger: 'API + SSO/SAML + White Label + Isolamento RLS',
    features: [
      'Operadores ilimitados',
      'Auditorias IA ilimitadas',
      'White Label completo',
      'Multi-tenant isolado com RLS',
      'API dedicada + SSO/SAML',
      'Controle total de usuarios e setores',
      'SLA garantido 99.9%',
      'Customer Success dedicado',
    ],
    highlighted: false,
    cta: 'Falar com Vendas',
  },
];

const FAQ_ITEMS = [
  {
    question: 'O que garante a precisao da "Falha Grave Zera"?',
    answer: 'O sistema e baseado no seu scorecard personalizado. A IA identifica o descumprimento do item marcado como isFalhaGrave, zera a nota automaticamente e anexa o trecho exato da transcricao como prova tecnica. E uma regra deterministica — sem subjetividade.',
  },
  {
    question: 'O que acontece se um operador mudar de equipe ou setor?',
    answer: 'Diferente de CRMs comuns, possuimos um Historico de Hierarquia Inviolavel. O desempenho do operador permanece vinculado ao supervisor e setor da epoca da avaliacao, garantindo que seus KPIs historicos nunca sejam corrompidos.',
  },
  {
    question: 'Como a plataforma garante o isolamento dos dados de audio?',
    answer: 'Utilizamos arquitetura multi-tenant com RLS (Row Level Security). Isso significa que, no nivel mais profundo do banco de dados, seus audios e dados sao isolados e inacessiveis por qualquer outra conta. Conformidade com ISO 27001.',
  },
  {
    question: 'A auditoria de massa via IA e realmente confiavel?',
    answer: 'Sim. A IA utiliza o mesmo scorecard deterministico aplicado por humanos, garantindo cobertura de 100% das chamadas contra os 5% do modelo manual. Cada avaliacao inclui a transcricao mapeada aos itens do scorecard, eliminando o "ponto cego" da operacao.',
  },
  {
    question: 'Como funciona o feedback 3+3 do simulador?',
    answer: 'Ao concluir uma simulacao, a IA entrega feedback estruturado em JSON: 3 pontos positivos (o que o operador fez bem) e 3 pontos de melhoria (o que precisa corrigir). Diferente do feedback humano tardio, o reforco e imediato e tecnico.',
  },
  {
    question: 'Quanto tempo leva do setup ao primeiro resultado?',
    answer: 'O objetivo e "Time to Value" imediato. Etapa 1: Configure scorecards e ative isFalhaGrave (Dia 1). Etapa 2: Upload de audios para auditoria IA (Dia 2). Etapa 3: Habilite simulacoes por voz (Dia 3). Etapa 4: Monitore a queda de ate 72% nas zeradas em 4 semanas.',
  },
];

// ═══════════════════════════════════════════════════════
// COMPONENTES
// ═══════════════════════════════════════════════════════

function Navbar() {
  const navigate = useNavigate();
  const isDark = useThemeDetector();
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { label: 'Pilares', href: '#pillars' },
    { label: 'Resultados', href: '#results' },
    { label: 'Como Funciona', href: '#how-it-works' },
    { label: 'Planos', href: '#pricing' },
    { label: 'FAQ', href: '#faq' },
  ];

  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 50,
        backgroundColor: isScrolled ? 'var(--card)' : 'transparent',
        borderBottom: isScrolled ? '1px solid var(--border)' : '1px solid transparent',
        backdropFilter: isScrolled ? 'blur(16px)' : 'none',
        transition: 'all 0.3s ease',
      }}
    >
      <div style={{ maxWidth: '1280px', margin: '0 auto', padding: '0 24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: '72px' }}>
          <img
            src={isDark ? logoDarkImg : logoImg}
            alt="SimulaDesk"
            style={{ height: '40px', width: 'auto', objectFit: 'contain' }}
          />

          <nav style={{ display: 'flex', alignItems: 'center', gap: '32px' }} className="hidden md:flex">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                style={{
                  fontFamily: 'var(--font-family-inter)',
                  fontSize: 'var(--text-sm)',
                  fontWeight: 'var(--font-weight-medium)',
                  color: 'var(--foreground)',
                  textDecoration: 'none',
                  transition: 'color 0.2s',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--primary)')}
                onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--foreground)')}
              >
                {link.label}
              </a>
            ))}
          </nav>

          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }} className="hidden md:flex">
            <button
              onClick={() => navigate('/login')}
              style={{
                fontFamily: 'var(--font-family-inter)',
                fontSize: 'var(--text-sm)',
                fontWeight: 'var(--font-weight-medium)',
                color: 'var(--foreground)',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                padding: '8px 16px',
              }}
            >
              Entrar
            </button>
            <button
              onClick={() => navigate('/login')}
              style={{
                fontFamily: 'var(--font-family-inter)',
                fontSize: 'var(--text-sm)',
                fontWeight: 'var(--font-weight-semibold)',
                color: 'var(--primary-foreground)',
                backgroundColor: 'var(--primary)',
                border: 'none',
                borderRadius: 'var(--radius-button)',
                padding: '10px 24px',
                cursor: 'pointer',
                transition: 'opacity 0.2s',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.9')}
              onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
            >
              Teste Gratis 14 dias
            </button>
          </div>

          <button
            className="md:hidden"
            onClick={() => setMobileOpen(!mobileOpen)}
            style={{ background: 'none', border: 'none', color: 'var(--foreground)', cursor: 'pointer' }}
          >
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            className="md:hidden"
            style={{
              backgroundColor: 'var(--card)',
              borderTop: '1px solid var(--border)',
              padding: '16px 0',
            }}
          >
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={() => setMobileOpen(false)}
                style={{
                  display: 'block',
                  fontFamily: 'var(--font-family-inter)',
                  fontSize: 'var(--text-base)',
                  fontWeight: 'var(--font-weight-medium)',
                  color: 'var(--foreground)',
                  textDecoration: 'none',
                  padding: '12px 0',
                }}
              >
                {link.label}
              </a>
            ))}
            <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
              <button
                onClick={() => { setMobileOpen(false); navigate('/login'); }}
                style={{
                  flex: 1, fontFamily: 'var(--font-family-inter)', fontSize: 'var(--text-sm)',
                  fontWeight: 'var(--font-weight-medium)', color: 'var(--foreground)',
                  backgroundColor: 'var(--muted)', border: 'none',
                  borderRadius: 'var(--radius-button)', padding: '10px 16px', cursor: 'pointer',
                }}
              >
                Entrar
              </button>
              <button
                onClick={() => { setMobileOpen(false); navigate('/login'); }}
                style={{
                  flex: 1, fontFamily: 'var(--font-family-inter)', fontSize: 'var(--text-sm)',
                  fontWeight: 'var(--font-weight-semibold)', color: 'var(--primary-foreground)',
                  backgroundColor: 'var(--primary)', border: 'none',
                  borderRadius: 'var(--radius-button)', padding: '10px 16px', cursor: 'pointer',
                }}
              >
                Teste Gratis
              </button>
            </div>
          </motion.div>
        )}
      </div>
    </motion.header>
  );
}

// ── ANIMATED SIMULATOR MOCKUP ──
function SimulatorMockup() {
  const [phase, setPhase] = useState(0); // 0=typing-client, 1=client-done, 2=typing-operator, 3=operator-done, 4=eval-in
  const [clientText, setClientText] = useState('');
  const [operatorText, setOperatorText] = useState('');
  const [evalVisible, setEvalVisible] = useState(false);
  const [scoreValue, setScoreValue] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const inView = useInView(containerRef as React.RefObject<Element>, { once: true });

  const clientMsg = SIMULATOR_SEQUENCE[0].text;
  const operatorMsg = SIMULATOR_SEQUENCE[1].text;

  useEffect(() => {
    if (!inView) return;
    let cancelled = false;

    async function animate() {
      // Phase 0: wait then type client
      await wait(1000);
      if (cancelled) return;
      setPhase(0);
      for (let i = 1; i <= clientMsg.length; i++) {
        if (cancelled) return;
        setClientText(clientMsg.slice(0, i));
        await wait(25);
      }
      setPhase(1);

      // Phase 2: typing indicator then type operator
      await wait(1200);
      if (cancelled) return;
      setPhase(2);
      for (let i = 1; i <= operatorMsg.length; i++) {
        if (cancelled) return;
        setOperatorText(operatorMsg.slice(0, i));
        await wait(22);
      }
      setPhase(3);

      // Phase 4: show AI evaluation
      await wait(800);
      if (cancelled) return;
      setPhase(4);
      setEvalVisible(true);

      // Animate score
      for (let s = 0; s <= 87; s += 2) {
        if (cancelled) return;
        setScoreValue(Math.min(s, 87));
        await wait(15);
      }
    }

    animate();
    return () => { cancelled = true; };
  }, [inView, clientMsg, operatorMsg]);

  function wait(ms: number) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  const TypingIndicator = () => (
    <div style={{ display: 'flex', gap: '4px', alignItems: 'center', padding: '8px 0' }}>
      {[0, 1, 2].map((i) => (
        <motion.div
          key={i}
          animate={{ opacity: [0.3, 1, 0.3] }}
          transition={{ duration: 0.8, repeat: Infinity, delay: i * 0.2 }}
          style={{
            width: '7px', height: '7px', borderRadius: '50%',
            backgroundColor: 'var(--muted-foreground)',
          }}
        />
      ))}
    </div>
  );

  return (
    <div ref={containerRef} style={{
      borderRadius: 'var(--radius-card)',
      overflow: 'hidden',
      border: '1px solid var(--border)',
      backgroundColor: 'var(--card)',
      boxShadow: '0 25px 60px -12px rgba(0,0,0,0.12)',
    }}>
      {/* Header bar */}
      <div style={{
        padding: '12px 20px',
        backgroundColor: 'var(--primary)',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Headphones size={16} style={{ color: 'var(--primary-foreground)' }} />
          <span style={{
            fontFamily: 'var(--font-family-inter)', fontSize: 'var(--text-xs)',
            fontWeight: 'var(--font-weight-semibold)', color: 'var(--primary-foreground)',
          }}>
            Simulador SimulaDesk
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <motion.div
            animate={{ opacity: [1, 0.4, 1] }}
            transition={{ duration: 1.5, repeat: Infinity }}
            style={{
              width: '8px', height: '8px', borderRadius: '50%',
              backgroundColor: 'var(--success)',
            }}
          />
          <span style={{
            fontFamily: 'var(--font-family-mono)', fontSize: 'var(--text-xs)',
            color: 'var(--primary-foreground)', opacity: 0.8,
          }}>AO VIVO</span>
        </div>
      </div>

      {/* Scenario bar */}
      <div style={{
        padding: '10px 20px',
        backgroundColor: 'color-mix(in srgb, var(--primary) 8%, var(--card))',
        borderBottom: '1px solid var(--border)',
        display: 'flex', alignItems: 'center', gap: '8px',
      }}>
        <AlertTriangle size={13} style={{ color: 'var(--warning)' }} />
        <span style={{
          fontFamily: 'var(--font-family-inter)', fontSize: 'var(--text-xs)',
          color: 'var(--muted-foreground)',
        }}>
          Cenario: <strong style={{ color: 'var(--foreground)' }}>Cliente irritado com atraso na entrega</strong>
        </span>
        <span style={{
          marginLeft: 'auto',
          fontFamily: 'var(--font-family-mono)', fontSize: 'var(--text-micro)',
          color: 'var(--muted-foreground)',
          backgroundColor: 'var(--muted)',
          padding: '2px 8px', borderRadius: '999px',
        }}>Dificuldade: Alta</span>
      </div>

      {/* Chat area */}
      <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '14px', minHeight: '200px' }}>
        {/* Client message */}
        <AnimatePresence>
          {clientText && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}
            >
              <div style={{
                width: '32px', height: '32px', borderRadius: '50%',
                backgroundColor: 'color-mix(in srgb, var(--error) 12%, transparent)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}>
                <Users size={14} style={{ color: 'var(--error)' }} />
              </div>
              <div style={{
                backgroundColor: 'var(--muted)', borderRadius: 'var(--radius)',
                padding: '10px 14px', maxWidth: '82%',
              }}>
                <span style={{
                  fontFamily: 'var(--font-family-inter)', fontSize: '11px',
                  fontWeight: 'var(--font-weight-semibold)', color: 'var(--error)',
                  display: 'block', marginBottom: '3px',
                }}>Cliente IA (irritado)</span>
                <p style={{
                  fontFamily: 'var(--font-family-inter)', fontSize: 'var(--text-xs)',
                  color: 'var(--foreground)', lineHeight: 1.6, margin: 0,
                }}>
                  {clientText}
                  {phase === 0 && (
                    <motion.span
                      animate={{ opacity: [1, 0] }}
                      transition={{ duration: 0.5, repeat: Infinity }}
                      style={{ color: 'var(--primary)', fontWeight: 'bold' }}
                    >|</motion.span>
                  )}
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Typing indicator for operator */}
        {phase === 1 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', flexDirection: 'row-reverse' }}
          >
            <div style={{
              width: '32px', height: '32px', borderRadius: '50%',
              backgroundColor: 'color-mix(in srgb, var(--primary) 12%, transparent)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}>
              <Headphones size={14} style={{ color: 'var(--primary)' }} />
            </div>
            <div style={{
              backgroundColor: 'color-mix(in srgb, var(--primary) 8%, transparent)',
              borderRadius: 'var(--radius)', padding: '10px 14px',
            }}>
              <TypingIndicator />
            </div>
          </motion.div>
        )}

        {/* Operator message */}
        <AnimatePresence>
          {operatorText && phase >= 2 && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', flexDirection: 'row-reverse' }}
            >
              <div style={{
                width: '32px', height: '32px', borderRadius: '50%',
                backgroundColor: 'color-mix(in srgb, var(--primary) 12%, transparent)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}>
                <Headphones size={14} style={{ color: 'var(--primary)' }} />
              </div>
              <div style={{
                backgroundColor: 'color-mix(in srgb, var(--primary) 8%, transparent)',
                borderRadius: 'var(--radius)', padding: '10px 14px', maxWidth: '82%',
              }}>
                <span style={{
                  fontFamily: 'var(--font-family-inter)', fontSize: '11px',
                  fontWeight: 'var(--font-weight-semibold)', color: 'var(--primary)',
                  display: 'block', marginBottom: '3px',
                }}>Operador (voce)</span>
                <p style={{
                  fontFamily: 'var(--font-family-inter)', fontSize: 'var(--text-xs)',
                  color: 'var(--foreground)', lineHeight: 1.6, margin: 0,
                }}>
                  {operatorText}
                  {phase === 2 && (
                    <motion.span
                      animate={{ opacity: [1, 0] }}
                      transition={{ duration: 0.5, repeat: Infinity }}
                      style={{ color: 'var(--primary)', fontWeight: 'bold' }}
                    >|</motion.span>
                  )}
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* AI Evaluation - animated entrance */}
        <AnimatePresence>
          {evalVisible && (
            <motion.div
              initial={{ opacity: 0, y: 20, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.5, ease: 'easeOut' }}
              style={{
                backgroundColor: 'color-mix(in srgb, var(--secondary) 8%, transparent)',
                border: '1px solid color-mix(in srgb, var(--secondary) 20%, transparent)',
                borderRadius: 'var(--radius)', padding: '14px',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Brain size={14} style={{ color: 'var(--secondary)' }} />
                  <span style={{
                    fontFamily: 'var(--font-family-inter)', fontSize: '10px',
                    fontWeight: 'var(--font-weight-bold)', color: 'var(--secondary)',
                    textTransform: 'uppercase', letterSpacing: '0.05em',
                  }}>Avaliacao IA</span>
                </div>
                <div style={{
                  display: 'flex', alignItems: 'center', gap: '6px',
                  backgroundColor: 'color-mix(in srgb, var(--secondary) 15%, transparent)',
                  padding: '3px 10px', borderRadius: '999px',
                }}>
                  <Gauge size={12} style={{ color: 'var(--secondary)' }} />
                  <span style={{
                    fontFamily: 'var(--font-family-mono)', fontSize: 'var(--text-sm)',
                    fontWeight: 'var(--font-weight-bold)', color: 'var(--secondary)',
                  }}>{scoreValue}/100</span>
                </div>
              </div>

              {/* Score bar */}
              <div style={{
                height: '4px', borderRadius: '2px', backgroundColor: 'var(--border)',
                marginBottom: '12px', overflow: 'hidden',
              }}>
                <motion.div
                  initial={{ width: '0%' }}
                  animate={{ width: `${scoreValue}%` }}
                  style={{
                    height: '100%', borderRadius: '2px',
                    backgroundColor: 'var(--secondary)',
                  }}
                />
              </div>

              <div className="grid grid-cols-2 gap-8">
                <div>
                  <span style={{
                    fontFamily: 'var(--font-family-inter)', fontSize: '10px',
                    fontWeight: 'var(--font-weight-semibold)', color: 'var(--success)',
                    display: 'block', marginBottom: '5px',
                  }}>3 Pontos Positivos</span>
                  {['Empatia demonstrada', 'Chamou pelo nome', 'Assumiu responsabilidade'].map((p, idx) => (
                    <motion.div
                      key={p}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.2 + idx * 0.15 }}
                      style={{ display: 'flex', alignItems: 'center', gap: '5px', marginBottom: '3px' }}
                    >
                      <CheckCircle2 size={11} style={{ color: 'var(--success)' }} />
                      <span style={{ fontFamily: 'var(--font-family-inter)', fontSize: '11px', color: 'var(--foreground)' }}>{p}</span>
                    </motion.div>
                  ))}
                </div>
                <div>
                  <span style={{
                    fontFamily: 'var(--font-family-inter)', fontSize: '10px',
                    fontWeight: 'var(--font-weight-semibold)', color: 'var(--warning)',
                    display: 'block', marginBottom: '5px',
                  }}>3 Pontos de Melhoria</span>
                  {['Oferecer solucao concreta', 'Informar prazo especifico', 'Usar script de retencao'].map((p, idx) => (
                    <motion.div
                      key={p}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.2 + idx * 0.15 }}
                      style={{ display: 'flex', alignItems: 'center', gap: '5px', marginBottom: '3px' }}
                    >
                      <AlertTriangle size={11} style={{ color: 'var(--warning)' }} />
                      <span style={{ fontFamily: 'var(--font-family-inter)', fontSize: '11px', color: 'var(--foreground)' }}>{p}</span>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

// ── HERO: Simulador de Voo ──
function HeroSection() {
  const navigate = useNavigate();
  return (
    <section
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        paddingTop: '72px',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: `radial-gradient(ellipse at 20% 10%, color-mix(in srgb, var(--primary) 10%, transparent) 0%, transparent 50%),
                        radial-gradient(ellipse at 80% 90%, color-mix(in srgb, var(--secondary) 8%, transparent) 0%, transparent 50%)`,
        }}
      />

      <div style={{ maxWidth: '1280px', margin: '0 auto', padding: '0 24px', width: '100%', position: 'relative', zIndex: 1 }}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7 }}
          >
            {/* Badge */}
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                backgroundColor: 'color-mix(in srgb, var(--secondary) 12%, transparent)',
                borderRadius: '999px',
                padding: '6px 16px',
                marginBottom: '24px',
                border: '1px solid color-mix(in srgb, var(--secondary) 20%, transparent)',
              }}
            >
              <Sparkles size={14} style={{ color: 'var(--secondary)' }} />
              <span style={{
                fontFamily: 'var(--font-family-inter)',
                fontSize: 'var(--text-xs)',
                fontWeight: 'var(--font-weight-bold)',
                color: 'var(--secondary)',
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
              }}>
                O Simulador de Voo do Atendimento
              </span>
            </div>

            <h1 style={{
              fontFamily: 'var(--font-family-inter)',
              fontSize: 'clamp(var(--text-2xl), 5vw, var(--text-4xl))',
              fontWeight: 'var(--font-weight-semibold)',
              color: 'var(--foreground)',
              lineHeight: 1.08,
              marginBottom: '16px',
              letterSpacing: '-1.5px',
            }}>
              O{' '}
              <span style={{
                background: 'linear-gradient(135deg, var(--primary), var(--secondary))',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}>
                Simulador de Voo
              </span>{' '}
              do Atendimento.
            </h1>

            <p style={{
              fontFamily: 'var(--font-family-inter)',
              fontSize: 'var(--text-md)',
              fontWeight: 'var(--font-weight-medium)',
              color: 'var(--foreground)',
              lineHeight: 1.6,
              marginBottom: '24px',
              maxWidth: '560px',
            }}>
              Pare de treinar com seus clientes.{' '}
              <span style={{ color: 'var(--muted-foreground)' }}>
                Utilize IA para simular cenarios, auditar 100% das chamadas e garantir que a{' '}
              </span>
              <span style={{ color: 'var(--error)', fontWeight: 'var(--font-weight-semibold)' }}>Falha Grave Zera</span>
              <span style={{ color: 'var(--muted-foreground)' }}> no treino, nao na sua operacao.</span>
            </p>

            {/* Mini proof points */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '36px' }}>
              {[
                { icon: Brain, text: 'Loop de Maestria 3+3: feedback estruturado em JSON, imediato e tecnico', color: 'var(--secondary)' },
                { icon: ShieldAlert, text: 'isFalhaGrave = true → nota ZERO automatica com prova tecnica', color: 'var(--error)' },
                { icon: AudioLines, text: 'Upload MP3/WAV → transcricao + auditoria IA assincrona em lote', color: 'var(--primary)' },
              ].map((p) => (
                <div key={p.text} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <div style={{
                    width: '28px', height: '28px', borderRadius: '6px',
                    backgroundColor: `color-mix(in srgb, ${p.color} 12%, transparent)`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  }}>
                    <p.icon size={14} style={{ color: p.color }} />
                  </div>
                  <span style={{
                    fontFamily: 'var(--font-family-inter)',
                    fontSize: 'var(--text-sm)',
                    color: 'var(--foreground)',
                    fontWeight: 'var(--font-weight-medium)',
                  }}>
                    {p.text}
                  </span>
                </div>
              ))}
            </div>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', marginBottom: '40px' }}>
              <button
                onClick={() => navigate('/login')}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: '8px',
                  fontFamily: 'var(--font-family-inter)', fontSize: 'var(--text-base)',
                  fontWeight: 'var(--font-weight-semibold)', color: 'var(--primary-foreground)',
                  backgroundColor: 'var(--primary)', border: 'none',
                  borderRadius: 'var(--radius-button)', padding: '14px 32px',
                  cursor: 'pointer', transition: 'opacity 0.2s, transform 0.2s',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.9'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.transform = 'translateY(0)'; }}
              >
                Iniciar teste gratis de 14 dias <ArrowRight size={18} />
              </button>
              <button
                onClick={() => navigate('/demo')}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: '8px',
                  fontFamily: 'var(--font-family-inter)', fontSize: 'var(--text-base)',
                  fontWeight: 'var(--font-weight-medium)', color: 'var(--foreground)',
                  backgroundColor: 'transparent', border: '1px solid var(--border)',
                  borderRadius: 'var(--radius-button)', padding: '14px 32px',
                  cursor: 'pointer', transition: 'background-color 0.2s',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--muted)')}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
              >
                <Play size={18} style={{ color: 'var(--secondary)' }} /> Testar Simulador
              </button>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '20px', flexWrap: 'wrap' }}>
              {[
                { icon: Shield, text: 'ISO 27001' },
                { icon: Database, text: '38 tabelas + RLS' },
                { icon: Zap, text: 'Setup em 3 dias' },
                { icon: Clock, text: 'Valor em 4 semanas' },
              ].map((b) => (
                <div key={b.text} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <b.icon size={13} style={{ color: 'var(--muted-foreground)' }} />
                  <span style={{
                    fontFamily: 'var(--font-family-inter)',
                    fontSize: 'var(--text-xs)',
                    color: 'var(--muted-foreground)',
                  }}>{b.text}</span>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Right: Animated Simulator Mockup */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="hidden lg:block"
          >
            <SimulatorMockup />
          </motion.div>
        </div>
      </div>
    </section>
  );
}

// ── STATS ANIMADOS ──
function StatsSection() {
  return (
    <section id="results" style={{
      padding: '80px 24px',
      background: 'linear-gradient(135deg, var(--primary) 0%, color-mix(in srgb, var(--primary) 80%, var(--secondary)) 100%)',
    }}>
      <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          style={{
            fontFamily: 'var(--font-family-inter)', fontSize: 'var(--text-sm)',
            fontWeight: 'var(--font-weight-semibold)', color: 'var(--primary-foreground)',
            textAlign: 'center', textTransform: 'uppercase', letterSpacing: '0.1em',
            opacity: 0.7, marginBottom: '40px',
          }}
        >
          KPIs de sucesso dos nossos clientes
        </motion.p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          {STATS.map((stat, i) => {
            const counter = useCounter(stat.value, 2000);
            return (
              <motion.div
                key={stat.label}
                ref={counter.ref}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.12 }}
                style={{ textAlign: 'center' }}
              >
                <div style={{
                  fontFamily: 'var(--font-family-mono)',
                  fontSize: 'clamp(var(--text-2xl), 4vw, var(--text-4xl))',
                  fontWeight: 'var(--font-weight-bold)',
                  color: 'var(--primary-foreground)',
                  lineHeight: 1,
                  marginBottom: '8px',
                }}>
                  {stat.prefix}{counter.count}{stat.suffix}
                </div>
                <div style={{
                  fontFamily: 'var(--font-family-inter)',
                  fontSize: 'var(--text-base)',
                  fontWeight: 'var(--font-weight-semibold)',
                  color: 'var(--primary-foreground)',
                  opacity: 0.95,
                }}>
                  {stat.label}
                </div>
                <div style={{
                  fontFamily: 'var(--font-family-inter)',
                  fontSize: 'var(--text-xs)',
                  color: 'var(--primary-foreground)',
                  opacity: 0.7, marginTop: '4px',
                }}>
                  {stat.sublabel}
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ── 5 PILARES (Deep-dive cards) ──
function PillarsSection() {
  return (
    <section id="pillars" style={{ padding: '100px 24px', backgroundColor: 'var(--background)' }}>
      <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          style={{ textAlign: 'center', marginBottom: '72px' }}
        >
          <span style={{
            fontFamily: 'var(--font-family-inter)', fontSize: 'var(--text-xs)',
            fontWeight: 'var(--font-weight-bold)', color: 'var(--primary)',
            textTransform: 'uppercase', letterSpacing: '0.12em',
          }}>
            5 Pilares Estrategicos
          </span>
          <h2 style={{
            fontFamily: 'var(--font-family-inter)',
            fontSize: 'clamp(var(--text-xl), 3vw, var(--text-3xl))',
            fontWeight: 'var(--font-weight-semibold)',
            color: 'var(--foreground)',
            marginTop: '12px', marginBottom: '16px', letterSpacing: '-0.5px',
          }}>
            Solucoes deterministicas para gargalos de escala e conformidade
          </h2>
          <p style={{
            fontFamily: 'var(--font-family-inter)', fontSize: 'var(--text-base)',
            color: 'var(--muted-foreground)', maxWidth: '680px', margin: '0 auto', lineHeight: 1.7,
          }}>
            Cada pilar resolve uma dor critica do Analista de Qualidade e do Operador
            de forma automatica, escalavel e sem subjetividade.
          </p>
        </motion.div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
          {PILLARS.map((pillar, i) => {
            const isEven = i % 2 === 0;
            return (
              <motion.div
                key={pillar.id}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-50px' }}
                transition={{ duration: 0.5 }}
                style={{
                  backgroundColor: 'var(--card)',
                  borderRadius: 'var(--radius-card)',
                  border: '1px solid var(--border)',
                  overflow: 'hidden',
                }}
              >
                <div
                  className={`grid grid-cols-1 lg:grid-cols-5`}
                  style={{ minHeight: '340px' }}
                >
                  {/* Content side (3 cols) */}
                  <div
                    className={`lg:col-span-3 ${isEven ? '' : 'lg:order-2'}`}
                    style={{ padding: '40px' }}
                  >
                    <div style={{
                      display: 'inline-flex', alignItems: 'center', gap: '8px',
                      backgroundColor: `color-mix(in srgb, ${pillar.color} 10%, transparent)`,
                      borderRadius: '999px', padding: '4px 14px', marginBottom: '16px',
                    }}>
                      <pillar.icon size={14} style={{ color: pillar.color }} />
                      <span style={{
                        fontFamily: 'var(--font-family-inter)', fontSize: 'var(--text-xs)',
                        fontWeight: 'var(--font-weight-bold)', color: pillar.color,
                        textTransform: 'uppercase', letterSpacing: '0.06em',
                      }}>{pillar.badge}</span>
                    </div>

                    <h3 style={{
                      fontFamily: 'var(--font-family-inter)',
                      fontSize: 'var(--text-xl)',
                      fontWeight: 'var(--font-weight-bold)',
                      color: 'var(--foreground)',
                      marginBottom: '4px', letterSpacing: '-0.3px',
                    }}>{pillar.title}</h3>

                    <p style={{
                      fontFamily: 'var(--font-family-inter)', fontSize: 'var(--text-sm)',
                      fontWeight: 'var(--font-weight-semibold)', color: pillar.color,
                      marginBottom: '8px',
                    }}>{pillar.subtitle}</p>

                    {pillar.painPoint && (
                      <p style={{
                        fontFamily: 'var(--font-family-mono)', fontSize: 'var(--text-xs)',
                        color: 'var(--muted-foreground)',
                        marginBottom: '12px',
                        padding: '4px 10px',
                        backgroundColor: 'var(--muted)',
                        borderRadius: 'var(--radius-input)',
                        display: 'inline-block',
                      }}>{pillar.painPoint}</p>
                    )}

                    <p style={{
                      fontFamily: 'var(--font-family-inter)', fontSize: 'var(--text-sm)',
                      color: 'var(--muted-foreground)', lineHeight: 1.8, marginBottom: '20px',
                    }}>{pillar.description}</p>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {pillar.highlights.map((h) => (
                        <div key={h} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <CheckCircle2 size={14} style={{ color: pillar.color, flexShrink: 0 }} />
                          <span style={{
                            fontFamily: 'var(--font-family-inter)', fontSize: 'var(--text-sm)',
                            color: 'var(--foreground)',
                          }}>{h}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Metric side (2 cols) */}
                  <div
                    className={`lg:col-span-2 ${isEven ? '' : 'lg:order-1'}`}
                    style={{
                      background: `linear-gradient(135deg, ${pillar.color}, color-mix(in srgb, ${pillar.color} 70%, var(--card)))`,
                      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                      padding: '40px', textAlign: 'center', minHeight: '200px',
                    }}
                  >
                    <pillar.icon size={48} style={{ color: 'rgba(255,255,255,0.2)', marginBottom: '16px' }} />
                    <div style={{
                      fontFamily: 'var(--font-family-mono)',
                      fontSize: 'var(--text-4xl)',
                      fontWeight: 'var(--font-weight-bold)',
                      color: '#fff',
                      lineHeight: 1,
                      marginBottom: '8px',
                    }}>{pillar.metric.value}</div>
                    <div style={{
                      fontFamily: 'var(--font-family-inter)',
                      fontSize: 'var(--text-sm)',
                      fontWeight: 'var(--font-weight-medium)',
                      color: 'rgba(255,255,255,0.85)',
                      maxWidth: '200px',
                    }}>{pillar.metric.label}</div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ── COMO FUNCIONA: Flow visual ──
function HowItWorksSection() {
  const steps = [
    { icon: FileCheck, title: 'Configure', desc: 'Defina seus scorecards e ative o boolean isFalhaGrave nos itens criticos de conduta e regulatorio.', color: 'var(--primary)', day: 'Dia 1' },
    { icon: AudioLines, title: 'Audite', desc: 'Upload manual de audios MP3/WAV. A IA inicia transcricao e auditoria automatica instantaneamente.', color: 'var(--secondary)', day: 'Dia 2' },
    { icon: Mic, title: 'Treine', desc: 'Habilite os 3 cenarios de simulacao por voz. Operador pratica e recebe feedback 3+3 sem supervisao.', color: 'var(--warning)', day: 'Dia 3' },
    { icon: TrendingUp, title: 'Evolua', desc: 'Monitore a queda de ate 72% nas zeradas e a evolucao das notas nos Dashboards de Setores e Ligas.', color: 'var(--success)', day: 'Semana 4' },
  ];

  return (
    <section id="how-it-works" style={{ padding: '100px 24px', backgroundColor: 'var(--muted)' }}>
      <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          style={{ textAlign: 'center', marginBottom: '64px' }}
        >
          <span style={{
            fontFamily: 'var(--font-family-inter)', fontSize: 'var(--text-xs)',
            fontWeight: 'var(--font-weight-bold)', color: 'var(--secondary)',
            textTransform: 'uppercase', letterSpacing: '0.1em',
          }}>
            Time to Value Imediato
          </span>
          <h2 style={{
            fontFamily: 'var(--font-family-inter)',
            fontSize: 'clamp(var(--text-xl), 3vw, var(--text-3xl))',
            fontWeight: 'var(--font-weight-semibold)', color: 'var(--foreground)',
            marginTop: '12px', letterSpacing: '-0.5px',
          }}>
            Do Setup ao First Value em 3 dias
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {steps.map((step, i) => (
            <motion.div
              key={step.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.12 }}
              style={{
                backgroundColor: 'var(--card)',
                borderRadius: 'var(--radius-card)',
                border: '1px solid var(--border)',
                padding: '32px 24px',
                textAlign: 'center',
                position: 'relative',
              }}
            >
              <div style={{
                position: 'absolute', top: '16px', right: '16px',
                display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px',
              }}>
                <span style={{
                  fontFamily: 'var(--font-family-mono)', fontSize: 'var(--text-xs)',
                  fontWeight: 'var(--font-weight-bold)', color: step.color,
                  backgroundColor: `color-mix(in srgb, ${step.color} 10%, transparent)`,
                  padding: '2px 8px', borderRadius: '999px',
                }}>{step.day}</span>
              </div>

              <div style={{
                width: '56px', height: '56px', borderRadius: '50%',
                backgroundColor: step.color, display: 'flex',
                alignItems: 'center', justifyContent: 'center',
                margin: '0 auto 20px',
              }}>
                <step.icon size={24} style={{ color: '#fff' }} />
              </div>

              <h3 style={{
                fontFamily: 'var(--font-family-inter)', fontSize: 'var(--text-lg)',
                fontWeight: 'var(--font-weight-bold)', color: 'var(--foreground)',
                marginBottom: '8px',
              }}>{step.title}</h3>

              <p style={{
                fontFamily: 'var(--font-family-inter)', fontSize: 'var(--text-sm)',
                color: 'var(--muted-foreground)', lineHeight: 1.7,
              }}>{step.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── DEPOIMENTOS ──
function TestimonialsSection() {
  return (
    <section id="testimonials" style={{ padding: '100px 24px', backgroundColor: 'var(--background)' }}>
      <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          style={{ textAlign: 'center', marginBottom: '64px' }}
        >
          <span style={{
            fontFamily: 'var(--font-family-inter)', fontSize: 'var(--text-xs)',
            fontWeight: 'var(--font-weight-bold)', color: 'var(--primary)',
            textTransform: 'uppercase', letterSpacing: '0.1em',
          }}>
            Quem ja usa
          </span>
          <h2 style={{
            fontFamily: 'var(--font-family-inter)',
            fontSize: 'clamp(var(--text-xl), 3vw, var(--text-3xl))',
            fontWeight: 'var(--font-weight-semibold)', color: 'var(--foreground)',
            marginTop: '12px', letterSpacing: '-0.5px',
          }}>
            Resultados que falam por si
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {TESTIMONIALS.map((t, i) => (
            <motion.div
              key={t.name}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.15 }}
              style={{
                backgroundColor: 'var(--card)',
                borderRadius: 'var(--radius-card)',
                border: '1px solid var(--border)',
                padding: '32px',
                display: 'flex', flexDirection: 'column',
              }}
            >
              <div style={{ display: 'flex', gap: '2px', marginBottom: '16px' }}>
                {Array.from({ length: t.rating }).map((_, j) => (
                  <Star key={j} size={16} style={{ color: 'var(--warning)', fill: 'var(--warning)' }} />
                ))}
              </div>
              <p style={{
                fontFamily: 'var(--font-family-inter)', fontSize: 'var(--text-sm)',
                color: 'var(--foreground)', lineHeight: 1.8, marginBottom: '24px',
                fontStyle: 'italic', flex: 1,
              }}>
                "{t.text}"
              </p>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <ImageWithFallback
                  src={t.image}
                  alt={t.name}
                  style={{ width: '44px', height: '44px', borderRadius: '50%', objectFit: 'cover' }}
                />
                <div>
                  <div style={{
                    fontFamily: 'var(--font-family-inter)', fontSize: 'var(--text-sm)',
                    fontWeight: 'var(--font-weight-semibold)', color: 'var(--foreground)',
                  }}>{t.name}</div>
                  <div style={{
                    fontFamily: 'var(--font-family-inter)', fontSize: 'var(--text-xs)',
                    color: 'var(--muted-foreground)',
                  }}>{t.role}, {t.company}</div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── VIDEO WALKTHROUGH ──
function VideoWalkthroughSection() {
  const [isPlaying, setIsPlaying] = useState(false);

  const walkthroughSteps = [
    { time: '0:00', title: 'Configurando Questionarios', desc: 'Crie scorecards com falhas graves e pesos personalizados', icon: FileCheck },
    { time: '1:20', title: 'Simulador de Voo em Acao', desc: 'Veja um operador treinando com a IA em cenario de retencao', icon: Headphones },
    { time: '2:45', title: 'Auditoria de Massa com IA', desc: 'Upload de audios e avaliacao automatica em lote', icon: AudioLines },
    { time: '3:50', title: 'Dashboard de Resultados', desc: 'Metricas em tempo real com queda nas zeradas por falha grave', icon: BarChart3 },
    { time: '5:10', title: 'Gamificacao & Certificados', desc: 'Ligas, badges e certificados automaticos para o time', icon: Trophy },
  ];

  return (
    <section id="walkthrough" style={{ padding: '100px 24px', backgroundColor: 'var(--muted)' }}>
      <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          style={{ textAlign: 'center', marginBottom: '64px' }}
        >
          <span style={{
            fontFamily: 'var(--font-family-inter)', fontSize: 'var(--text-xs)',
            fontWeight: 'var(--font-weight-bold)', color: 'var(--secondary)',
            textTransform: 'uppercase', letterSpacing: '0.1em',
          }}>
            Veja na Pratica
          </span>
          <h2 style={{
            fontFamily: 'var(--font-family-inter)',
            fontSize: 'clamp(var(--text-xl), 3vw, var(--text-3xl))',
            fontWeight: 'var(--font-weight-semibold)', color: 'var(--foreground)',
            marginTop: '12px', marginBottom: '16px', letterSpacing: '-0.5px',
          }}>
            Walkthrough completo em 6 minutos
          </h2>
          <p style={{
            fontFamily: 'var(--font-family-inter)', fontSize: 'var(--text-base)',
            color: 'var(--muted-foreground)', maxWidth: '560px', margin: '0 auto',
          }}>
            Veja como configurar, treinar e auditar do zero ate os resultados.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 items-start">
          {/* Video player area */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="lg:col-span-3"
            style={{
              borderRadius: 'var(--radius-card)',
              overflow: 'hidden',
              border: '1px solid var(--border)',
              backgroundColor: 'var(--card)',
              position: 'relative',
            }}
          >
            {/* Video placeholder with play button */}
            <div
              onClick={() => setIsPlaying(!isPlaying)}
              style={{
                aspectRatio: '16/9',
                background: `linear-gradient(135deg, 
                  color-mix(in srgb, var(--primary) 90%, black) 0%, 
                  color-mix(in srgb, var(--secondary) 80%, var(--primary)) 100%)`,
                display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', position: 'relative',
                overflow: 'hidden',
              }}
            >
              {/* Decorative grid overlay */}
              <div style={{
                position: 'absolute', inset: 0,
                backgroundImage: `linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)`,
                backgroundSize: '40px 40px',
              }} />

              {/* Decorative elements */}
              <div style={{
                position: 'absolute', top: '20px', left: '20px',
                display: 'flex', alignItems: 'center', gap: '8px',
              }}>
                <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: 'rgba(255,255,255,0.15)' }} />
                <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: 'rgba(255,255,255,0.15)' }} />
                <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: 'rgba(255,255,255,0.15)' }} />
              </div>

              {!isPlaying ? (
                <>
                  <motion.div
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                    style={{
                      width: '80px', height: '80px', borderRadius: '50%',
                      backgroundColor: 'rgba(255,255,255,0.2)',
                      backdropFilter: 'blur(8px)',
                      border: '2px solid rgba(255,255,255,0.3)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      marginBottom: '16px', position: 'relative', zIndex: 1,
                    }}
                  >
                    <Play size={32} style={{ color: '#fff', marginLeft: '4px' }} fill="#fff" />
                  </motion.div>
                  <span style={{
                    fontFamily: 'var(--font-family-inter)', fontSize: 'var(--text-sm)',
                    fontWeight: 'var(--font-weight-semibold)', color: '#fff',
                    position: 'relative', zIndex: 1,
                  }}>
                    Clique para assistir o walkthrough
                  </span>
                  <span style={{
                    fontFamily: 'var(--font-family-mono)', fontSize: 'var(--text-xs)',
                    color: 'rgba(255,255,255,0.6)', marginTop: '4px',
                    position: 'relative', zIndex: 1,
                  }}>6:24</span>
                </>
              ) : (
                <div style={{
                  position: 'relative', zIndex: 1, textAlign: 'center',
                  padding: '0 40px',
                }}>
                  {/* Simulated playing state */}
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
                    style={{ marginBottom: '16px' }}
                  >
                    <Brain size={40} style={{ color: 'rgba(255,255,255,0.8)' }} />
                  </motion.div>
                  <p style={{
                    fontFamily: 'var(--font-family-inter)', fontSize: 'var(--text-base)',
                    fontWeight: 'var(--font-weight-semibold)', color: '#fff',
                    marginBottom: '8px',
                  }}>
                    Demonstracao em producao
                  </p>
                  <p style={{
                    fontFamily: 'var(--font-family-inter)', fontSize: 'var(--text-sm)',
                    color: 'rgba(255,255,255,0.7)',
                  }}>
                    O video sera integrado com seu conteudo real. Use o botao "Testar Simulador" para experimentar a demo interativa.
                  </p>
                  <button
                    onClick={(e) => { e.stopPropagation(); setIsPlaying(false); }}
                    style={{
                      marginTop: '16px', fontFamily: 'var(--font-family-inter)',
                      fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-medium)',
                      color: '#fff', backgroundColor: 'rgba(255,255,255,0.2)',
                      border: '1px solid rgba(255,255,255,0.3)',
                      borderRadius: 'var(--radius-button)', padding: '8px 20px',
                      cursor: 'pointer',
                    }}
                  >
                    Fechar
                  </button>
                </div>
              )}
            </div>
          </motion.div>

          {/* Chapter list */}
          <div className="lg:col-span-2" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {walkthroughSteps.map((step, i) => (
              <motion.div
                key={step.time}
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                style={{
                  display: 'flex', alignItems: 'center', gap: '14px',
                  padding: '14px 16px',
                  backgroundColor: 'var(--card)',
                  borderRadius: 'var(--radius)',
                  border: '1px solid var(--border)',
                  cursor: 'pointer',
                  transition: 'border-color 0.2s',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.borderColor = 'var(--primary)')}
                onMouseLeave={(e) => (e.currentTarget.style.borderColor = 'var(--border)')}
              >
                <div style={{
                  width: '36px', height: '36px', borderRadius: 'var(--radius-input)',
                  backgroundColor: `color-mix(in srgb, var(--primary) 10%, transparent)`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                  <step.icon size={16} style={{ color: 'var(--primary)' }} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{
                      fontFamily: 'var(--font-family-mono)', fontSize: 'var(--text-xs)',
                      color: 'var(--primary)', fontWeight: 'var(--font-weight-semibold)',
                    }}>{step.time}</span>
                    <span style={{
                      fontFamily: 'var(--font-family-inter)', fontSize: 'var(--text-sm)',
                      fontWeight: 'var(--font-weight-semibold)', color: 'var(--foreground)',
                    }}>{step.title}</span>
                  </div>
                  <p style={{
                    fontFamily: 'var(--font-family-inter)', fontSize: 'var(--text-xs)',
                    color: 'var(--muted-foreground)', margin: 0, marginTop: '2px',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>{step.desc}</p>
                </div>
                <Play size={14} style={{ color: 'var(--muted-foreground)', flexShrink: 0 }} />
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// ── PLANOS ──
function PricingSection() {
  const navigate = useNavigate();
  return (
    <section id="pricing" style={{ padding: '100px 24px', backgroundColor: 'var(--muted)' }}>
      <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          style={{ textAlign: 'center', marginBottom: '64px' }}
        >
          <span style={{
            fontFamily: 'var(--font-family-inter)', fontSize: 'var(--text-xs)',
            fontWeight: 'var(--font-weight-bold)', color: 'var(--secondary)',
            textTransform: 'uppercase', letterSpacing: '0.1em',
          }}>
            Matriz de Conversao por Plano
          </span>
          <h2 style={{
            fontFamily: 'var(--font-family-inter)',
            fontSize: 'clamp(var(--text-xl), 3vw, var(--text-3xl))',
            fontWeight: 'var(--font-weight-semibold)', color: 'var(--foreground)',
            marginTop: '12px', marginBottom: '16px', letterSpacing: '-0.5px',
          }}>
            Do Starter ao Enterprise com controle total
          </h2>
          <p style={{
            fontFamily: 'var(--font-family-inter)', fontSize: 'var(--text-base)',
            color: 'var(--muted-foreground)',
          }}>
            Acesso total as funcionalidades Professional por 14 dias. Sem cartao de credito. Setup em 3 dias.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-stretch">
          {PLANS.map((plan, i) => (
            <motion.div
              key={plan.name}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.15 }}
              style={{
                backgroundColor: plan.highlighted ? 'var(--primary)' : 'var(--card)',
                borderRadius: 'var(--radius-card)',
                border: plan.highlighted ? '2px solid var(--primary)' : '1px solid var(--border)',
                padding: '40px 32px',
                display: 'flex', flexDirection: 'column',
                position: 'relative',
                transform: plan.highlighted ? 'scale(1.04)' : 'none',
              }}
            >
              {plan.highlighted && (
                <div style={{
                  position: 'absolute', top: '-14px', left: '50%', transform: 'translateX(-50%)',
                  backgroundColor: 'var(--secondary)', color: 'var(--secondary-foreground)',
                  fontFamily: 'var(--font-family-inter)', fontSize: 'var(--text-xs)',
                  fontWeight: 'var(--font-weight-bold)', padding: '4px 16px',
                  borderRadius: '999px', textTransform: 'uppercase', letterSpacing: '0.05em',
                }}>
                  Mais Popular
                </div>
              )}

              <h3 style={{
                fontFamily: 'var(--font-family-inter)', fontSize: 'var(--text-lg)',
                fontWeight: 'var(--font-weight-semibold)',
                color: plan.highlighted ? 'var(--primary-foreground)' : 'var(--foreground)',
                marginBottom: '8px',
              }}>{plan.name}</h3>

              <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px', marginBottom: '8px' }}>
                <span style={{
                  fontFamily: 'var(--font-family-inter)', fontSize: 'var(--text-3xl)',
                  fontWeight: 'var(--font-weight-bold)',
                  color: plan.highlighted ? 'var(--primary-foreground)' : 'var(--foreground)',
                }}>{plan.price}</span>
                {plan.period && (
                  <span style={{
                    fontFamily: 'var(--font-family-inter)', fontSize: 'var(--text-sm)',
                    color: plan.highlighted ? 'rgba(255,255,255,0.7)' : 'var(--muted-foreground)',
                  }}>{plan.period}</span>
                )}
              </div>

              <p style={{
                fontFamily: 'var(--font-family-inter)', fontSize: 'var(--text-sm)',
                color: plan.highlighted ? 'rgba(255,255,255,0.8)' : 'var(--muted-foreground)',
                marginBottom: '16px', lineHeight: 1.6,
              }}>{plan.description}</p>

              {plan.trigger && (
                <div style={{
                  display: 'inline-flex', alignItems: 'center', gap: '6px',
                  padding: '4px 12px', borderRadius: '999px',
                  backgroundColor: plan.highlighted ? 'rgba(255,255,255,0.15)' : 'var(--muted)',
                  marginBottom: '24px',
                }}>
                  <Target size={12} style={{ color: plan.highlighted ? 'rgba(255,255,255,0.8)' : 'var(--primary)' }} />
                  <span style={{
                    fontFamily: 'var(--font-family-mono)', fontSize: 'var(--text-xs)',
                    color: plan.highlighted ? 'rgba(255,255,255,0.8)' : 'var(--primary)',
                    fontWeight: 'var(--font-weight-medium)',
                  }}>{plan.trigger}</span>
                </div>
              )}

              <div style={{ flex: 1, marginBottom: '32px' }}>
                {plan.features.map((f) => (
                  <div key={f} style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
                    <CheckCircle2 size={15} style={{ color: plan.highlighted ? 'rgba(255,255,255,0.9)' : 'var(--success)', flexShrink: 0 }} />
                    <span style={{
                      fontFamily: 'var(--font-family-inter)', fontSize: 'var(--text-sm)',
                      color: plan.highlighted ? 'rgba(255,255,255,0.9)' : 'var(--foreground)',
                    }}>{f}</span>
                  </div>
                ))}
              </div>

              <button
                onClick={() => navigate('/login')}
                style={{
                  width: '100%', fontFamily: 'var(--font-family-inter)', fontSize: 'var(--text-sm)',
                  fontWeight: 'var(--font-weight-semibold)',
                  color: plan.highlighted ? 'var(--primary)' : 'var(--primary-foreground)',
                  backgroundColor: plan.highlighted ? 'var(--primary-foreground)' : 'var(--primary)',
                  border: 'none', borderRadius: 'var(--radius-button)', padding: '14px 24px',
                  cursor: 'pointer', transition: 'opacity 0.2s',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.9')}
                onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
              >{plan.cta}</button>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── FAQ ──
function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <section id="faq" style={{ padding: '100px 24px', backgroundColor: 'var(--background)' }}>
      <div style={{ maxWidth: '768px', margin: '0 auto' }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          style={{ textAlign: 'center', marginBottom: '64px' }}
        >
          <span style={{
            fontFamily: 'var(--font-family-inter)', fontSize: 'var(--text-xs)',
            fontWeight: 'var(--font-weight-bold)', color: 'var(--primary)',
            textTransform: 'uppercase', letterSpacing: '0.1em',
          }}>FAQ</span>
          <h2 style={{
            fontFamily: 'var(--font-family-inter)',
            fontSize: 'clamp(var(--text-xl), 3vw, var(--text-3xl))',
            fontWeight: 'var(--font-weight-semibold)', color: 'var(--foreground)',
            marginTop: '12px', letterSpacing: '-0.5px',
          }}>Perguntas Frequentes</h2>
        </motion.div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {FAQ_ITEMS.map((item, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.05 }}
              style={{
                backgroundColor: 'var(--card)', borderRadius: 'var(--radius-card)',
                border: '1px solid var(--border)', overflow: 'hidden',
              }}
            >
              <button
                onClick={() => setOpenIndex(openIndex === i ? null : i)}
                style={{
                  width: '100%', display: 'flex', alignItems: 'center',
                  justifyContent: 'space-between', padding: '20px 24px',
                  background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left',
                }}
              >
                <span style={{
                  fontFamily: 'var(--font-family-inter)', fontSize: 'var(--text-base)',
                  fontWeight: 'var(--font-weight-medium)', color: 'var(--foreground)',
                }}>{item.question}</span>
                <ChevronDown
                  size={20}
                  style={{
                    color: 'var(--muted-foreground)', transition: 'transform 0.2s',
                    transform: openIndex === i ? 'rotate(180deg)' : 'rotate(0deg)',
                    flexShrink: 0, marginLeft: '16px',
                  }}
                />
              </button>
              {openIndex === i && (
                <div style={{ padding: '0 24px 20px' }}>
                  <p style={{
                    fontFamily: 'var(--font-family-inter)', fontSize: 'var(--text-sm)',
                    color: 'var(--muted-foreground)', lineHeight: 1.8,
                  }}>{item.answer}</p>
                </div>
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── CTA FINAL ──
function CTASection() {
  const navigate = useNavigate();
  return (
    <section style={{
      padding: '100px 24px',
      background: 'linear-gradient(135deg, var(--primary) 0%, var(--secondary) 100%)',
      position: 'relative', overflow: 'hidden',
    }}>
      <div style={{
        position: 'absolute', top: '-120px', right: '-120px',
        width: '400px', height: '400px', borderRadius: '50%', backgroundColor: 'rgba(255,255,255,0.05)',
      }} />
      <div style={{
        position: 'absolute', bottom: '-100px', left: '-100px',
        width: '350px', height: '350px', borderRadius: '50%', backgroundColor: 'rgba(255,255,255,0.05)',
      }} />

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        style={{ maxWidth: '720px', margin: '0 auto', textAlign: 'center', position: 'relative', zIndex: 1 }}
      >
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: '8px',
          backgroundColor: 'rgba(255,255,255,0.15)', borderRadius: '999px',
          padding: '6px 16px', marginBottom: '24px',
        }}>
          <Zap size={14} style={{ color: '#fff' }} />
          <span style={{
            fontFamily: 'var(--font-family-inter)', fontSize: 'var(--text-xs)',
            fontWeight: 'var(--font-weight-bold)', color: '#fff',
            textTransform: 'uppercase', letterSpacing: '0.06em',
          }}>Time to Value Imediato</span>
        </div>

        <h2 style={{
          fontFamily: 'var(--font-family-inter)',
          fontSize: 'clamp(var(--text-xl), 3vw, var(--text-3xl))',
          fontWeight: 'var(--font-weight-semibold)', color: '#fff',
          marginBottom: '20px', letterSpacing: '-0.5px',
        }}>
          Pare de treinar com seus clientes.{' '}
          Comece no simulador hoje.
        </h2>

        <p style={{
          fontFamily: 'var(--font-family-inter)', fontSize: 'var(--text-lg)',
          color: 'rgba(255,255,255,0.85)', marginBottom: '40px', lineHeight: 1.7,
        }}>
          Acesso total as funcionalidades Professional por 14 dias. Sem cartao de credito.
          Setup em 3 dias. Queda de ate 72% nas zeradas em 4 semanas.
        </p>

        <div style={{ display: 'flex', justifyContent: 'center', gap: '16px', flexWrap: 'wrap' }}>
          <button
            onClick={() => navigate('/login')}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '8px',
              fontFamily: 'var(--font-family-inter)', fontSize: 'var(--text-base)',
              fontWeight: 'var(--font-weight-semibold)', color: 'var(--primary)',
              backgroundColor: '#fff', border: 'none', borderRadius: 'var(--radius-button)',
              padding: '16px 40px', cursor: 'pointer',
              transition: 'transform 0.2s, box-shadow 0.2s',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.15)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
          >
            Iniciar meu teste gratis de 14 dias <ArrowRight size={18} />
          </button>
          <button
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '8px',
              fontFamily: 'var(--font-family-inter)', fontSize: 'var(--text-base)',
              fontWeight: 'var(--font-weight-medium)', color: '#fff',
              backgroundColor: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)',
              borderRadius: 'var(--radius-button)', padding: '16px 40px', cursor: 'pointer',
              transition: 'background-color 0.2s',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.25)')}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.15)')}
          >
            <MessageSquare size={18} /> Falar com Especialista
          </button>
        </div>
      </motion.div>
    </section>
  );
}

// ── ENTERPRISE READY TRUST SEALS ──
function EnterpriseReadySection() {
  const seals = [
    {
      icon: Shield,
      title: 'ISO 27001',
      subtitle: 'Seguranca de Dados',
      description: 'Conformidade rigorosa com o padrao internacional de seguranca da informacao. Criptografia end-to-end e backups automaticos.',
      color: 'var(--primary)',
    },
    {
      icon: Database,
      title: '38 Tabelas',
      subtitle: 'Integridade Referencial',
      description: 'Banco de dados estruturado com integridade referencial total. Historico cronologico de hierarquia inviolavel.',
      color: 'var(--secondary)',
    },
    {
      icon: Lock,
      title: 'RLS Ativado',
      subtitle: 'Isolamento Multi-Tenant',
      description: 'Privacidade garantida via Row Level Security. Os dados de cada empresa sao logicamente isolados no nivel do banco.',
      color: 'var(--warning)',
    },
  ];

  return (
    <section style={{ padding: '80px 24px', backgroundColor: 'var(--background)' }}>
      <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          style={{ textAlign: 'center', marginBottom: '48px' }}
        >
          <span style={{
            fontFamily: 'var(--font-family-inter)', fontSize: 'var(--text-xs)',
            fontWeight: 'var(--font-weight-bold)', color: 'var(--primary)',
            textTransform: 'uppercase', letterSpacing: '0.1em',
          }}>Enterprise Ready</span>
          <h2 style={{
            fontFamily: 'var(--font-family-inter)',
            fontSize: 'clamp(var(--text-lg), 2.5vw, var(--text-xl))',
            fontWeight: 'var(--font-weight-semibold)', color: 'var(--foreground)',
            marginTop: '8px', letterSpacing: '-0.3px',
          }}>Autoridade tecnica e seguranca de nivel corporativo</h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {seals.map((seal, i) => (
            <motion.div
              key={seal.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center',
                textAlign: 'center', padding: '32px 24px',
                backgroundColor: 'var(--card)', borderRadius: 'var(--radius-card)',
                border: '1px solid var(--border)',
              }}
            >
              <div style={{
                width: '56px', height: '56px', borderRadius: '50%',
                backgroundColor: `color-mix(in srgb, ${seal.color} 10%, transparent)`,
                border: `2px solid ${seal.color}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                marginBottom: '16px',
              }}>
                <seal.icon size={24} style={{ color: seal.color }} />
              </div>
              <span style={{
                fontFamily: 'var(--font-family-mono)', fontSize: 'var(--text-lg)',
                fontWeight: 'var(--font-weight-bold)', color: 'var(--foreground)',
                marginBottom: '2px',
              }}>{seal.title}</span>
              <span style={{
                fontFamily: 'var(--font-family-inter)', fontSize: 'var(--text-xs)',
                fontWeight: 'var(--font-weight-semibold)', color: seal.color,
                textTransform: 'uppercase', letterSpacing: '0.06em',
                marginBottom: '12px',
              }}>{seal.subtitle}</span>
              <p style={{
                fontFamily: 'var(--font-family-inter)', fontSize: 'var(--text-sm)',
                color: 'var(--muted-foreground)', lineHeight: 1.7, margin: 0,
              }}>{seal.description}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── FOOTER ──
function Footer() {
  const isDark = useThemeDetector();
  const year = new Date().getFullYear();

  const groups: { title: string; links: { label: string; href?: string }[] }[] = [
    { title: 'Produto', links: [{ label: 'Simulador IA' }, { label: 'Auditoria IA' }, { label: 'Falha Grave Zera' }, { label: 'Gamificacao' }, { label: 'Analytics' }] },
    { title: 'Empresa', links: [{ label: 'Sobre nos' }, { label: 'Blog' }, { label: 'Carreiras' }, { label: 'Parceiros' }, { label: 'Contato' }] },
    { title: 'Recursos', links: [{ label: 'Documentacao' }, { label: 'API' }, { label: 'Central de Ajuda' }, { label: 'Status' }, { label: 'Webinars' }] },
    { title: 'Legal', links: [{ label: 'Privacidade', href: '/privacidade' }, { label: 'Termos de Uso', href: '/termos' }, { label: 'LGPD', href: '/lgpd' }, { label: 'Cookies', href: '/cookies' }, { label: 'SLA', href: '/sla' }] },
  ];

  return (
    <footer style={{ padding: '64px 24px 32px', backgroundColor: 'var(--card)', borderTop: '1px solid var(--border)' }}>
      <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-8 mb-12">
          <div className="col-span-2 md:col-span-1">
            <img src={isDark ? logoDarkImg : logoImg} alt="SimulaDesk" style={{ height: '36px', width: 'auto', marginBottom: '16px' }} />
            <p style={{
              fontFamily: 'var(--font-family-inter)', fontSize: 'var(--text-sm)',
              color: 'var(--muted-foreground)', lineHeight: 1.7,
            }}>
              Motor de produtividade autonomo para atendimento. Treino, auditoria e gamificacao com IA.
            </p>
          </div>
          {groups.map((g) => (
            <div key={g.title}>
              <h4 style={{
                fontFamily: 'var(--font-family-inter)', fontSize: 'var(--text-sm)',
                fontWeight: 'var(--font-weight-semibold)', color: 'var(--foreground)', marginBottom: '16px',
              }}>{g.title}</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {g.links.map((l) => (
                  l.href ? (
                    <Link key={l.label} to={l.href} style={{
                      fontFamily: 'var(--font-family-inter)', fontSize: 'var(--text-sm)',
                      color: 'var(--muted-foreground)', textDecoration: 'none', transition: 'color 0.2s',
                    }}
                      onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--primary)')}
                      onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--muted-foreground)')}
                    >{l.label}</Link>
                  ) : (
                    <a key={l.label} href="#" style={{
                      fontFamily: 'var(--font-family-inter)', fontSize: 'var(--text-sm)',
                      color: 'var(--muted-foreground)', textDecoration: 'none', transition: 'color 0.2s',
                    }}
                      onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--primary)')}
                      onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--muted-foreground)')}
                    >{l.label}</a>
                  )
                ))}
              </div>
            </div>
          ))}
        </div>

        <div style={{
          borderTop: '1px solid var(--border)', paddingTop: '24px',
          display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: '16px',
        }}>
          <p style={{
            fontFamily: 'var(--font-family-inter)', fontSize: 'var(--text-xs)', color: 'var(--muted-foreground)',
          }}>&copy; {year} SimulaDesk. Todos os direitos reservados.</p>
          <div style={{ display: 'flex', gap: '16px' }}>
            {[
              { label: 'Privacidade', to: '/privacidade' },
              { label: 'Termos', to: '/termos' },
              { label: 'LGPD', to: '/lgpd' },
            ].map((t) => (
              <Link key={t.label} to={t.to} style={{
                fontFamily: 'var(--font-family-inter)', fontSize: 'var(--text-xs)',
                color: 'var(--muted-foreground)', textDecoration: 'none',
              }}>{t.label}</Link>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}

// ═══════════════════════════════════════════════════════
// LANDING PAGE
// ═══════════════════════════════════════════════════════

export function LandingPage() {
  return (
    <div style={{ backgroundColor: 'var(--background)', minHeight: '100vh' }}>
      <Navbar />
      <HeroSection />
      <StatsSection />
      <PillarsSection />
      <HowItWorksSection />
      <TestimonialsSection />
      <EnterpriseReadySection />
      <VideoWalkthroughSection />
      <PricingSection />
      <FAQSection />
      <CTASection />
      <Footer />
    </div>
  );
}

export default LandingPage;

